import * as React from 'react';

import { Button, Dialog, Typography2 } from '@coursera/cds-core';

import AccountLinkSlack from 'bundles/slack-account-link/components/AccountLinkSlack';
import {
  NotificationType,
  SlackAccountComponentNotification,
} from 'bundles/slack-account-link/components/SlackAccountComponentNotification';
import type { NotificationTypeType } from 'bundles/slack-account-link/components/SlackAccountComponentNotification';
import { UnlinkSlackButton } from 'bundles/slack-account-link/components/SlackAccountLinkingButtons';
import UnlinkSlackConfirm from 'bundles/slack-account-link/components/UnlinkSlackConfirm';
import { LINKED, SLACK_ACCOUNT_STATUS, VERIFY } from 'bundles/slack-account-link/constants';
import type { SLACK_ACCOUNT_STATUS_TYPE } from 'bundles/slack-account-link/constants';
import type { SlackAccount, SlackAccountStatus } from 'bundles/slack-account-link/types/SlackAccount';
import { link, unlink } from 'bundles/slack-account-link/utills/SlackAccountAPIUtils';

import _t from 'i18n!nls/third-party-auth';

export type SlackAccountComponentProps = {
  id: string;
  degreeId: string;
  email?: string | null;
  status: SLACK_ACCOUNT_STATUS_TYPE;
  slackTeamDomainName?: string;
};

export const NetworkOperationState = {
  IN_PROGRESS: 'IN_PROGRESS',
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
} as const;

export type NetworkOperationStateType = (typeof NetworkOperationState)[keyof typeof NetworkOperationState];

type State = {
  initialStatus: SlackAccountStatus;
  email: string;
  status: SlackAccountStatus;
  linkOperationStatus: null | NetworkOperationStateType;
  unlinkOperationStatus: null | NetworkOperationStateType;
  showUnlinkModal: boolean;
};

export class SlackAccountComponent extends React.Component<SlackAccountComponentProps, State> {
  constructor(props: SlackAccountComponentProps) {
    super(props);
    const { email, status } = props;

    this.state = {
      email: email || '',
      status,
      initialStatus: status,
      linkOperationStatus: null,
      unlinkOperationStatus: null,
      showUnlinkModal: false,
    };
  }

  onLink = (emailValue: string) => {
    const { degreeId } = this.props;
    this.setState({ linkOperationStatus: NetworkOperationState.IN_PROGRESS });

    link(degreeId, emailValue)
      .then((result: SlackAccount) => {
        const { email } = result;

        this.setState({
          email: email || '',
          status: SLACK_ACCOUNT_STATUS.EMAIL_VERIFIED,
          initialStatus: SLACK_ACCOUNT_STATUS.EMAIL_VERIFIED,
          linkOperationStatus: NetworkOperationState.SUCCESS,
        });
      })
      .catch(() => {
        this.setState({
          email: emailValue,
          status: SLACK_ACCOUNT_STATUS.UNLINKED,
          linkOperationStatus: NetworkOperationState.FAILED,
        });
      });
  };

  showUnlinkModal = () => {
    this.setState({ showUnlinkModal: true, linkOperationStatus: null });
  };

  onUnLink = () => {
    const { degreeId } = this.props;
    this.setState({ unlinkOperationStatus: NetworkOperationState.IN_PROGRESS });

    unlink(degreeId)
      .then(() => {
        this.setState({
          email: '',
          status: SLACK_ACCOUNT_STATUS.UNLINKED,
          unlinkOperationStatus: NetworkOperationState.SUCCESS,
        });
      })
      .catch(() => this.setState({ unlinkOperationStatus: NetworkOperationState.FAILED }));
  };

  onEdit = () => {
    this.setState({ status: SLACK_ACCOUNT_STATUS.UNLINKED, linkOperationStatus: null });
  };

  renderNotification = () => {
    const { slackTeamDomainName } = this.props;
    const { initialStatus, linkOperationStatus } = this.state;
    let status: null | NotificationTypeType = null;
    const signupLink = slackTeamDomainName
      ? `https://${slackTeamDomainName}.slack.com/signup`
      : 'https://slack.com/get-started';

    if (initialStatus === SLACK_ACCOUNT_STATUS.UNLINKED) {
      status = NotificationType.INFO;
    }

    if (linkOperationStatus === NetworkOperationState.FAILED) {
      status = NotificationType.LINK_ERROR;
    } else if (linkOperationStatus === NetworkOperationState.SUCCESS) {
      status = NotificationType.LINK_SUCCESS;
    }

    return (
      <SlackAccountComponentNotification
        notificationType={status}
        onDismiss={() => this.setState({ linkOperationStatus: null })}
        signupLink={signupLink}
      />
    );
  };

  renderUnlinkOpCompleteComp = () => {
    return (
      <React.Fragment>
        <Dialog.HeadingGroup>{_t('Your slack account has been unlinked.')}</Dialog.HeadingGroup>
        <Dialog.Content id="dialog-content">
          <Typography2 component="p">{_t('To deactivate your account completely, go to Slack.')}</Typography2>
        </Dialog.Content>
        <Dialog.Actions>
          <Button variant="primary" onClick={this.closeUnlinkModal}>
            {_t('Got it')}
          </Button>
        </Dialog.Actions>
      </React.Fragment>
    );
  };

  closeUnlinkModal = () => {
    this.setState({ showUnlinkModal: false, unlinkOperationStatus: null });
  };

  renderUnlinkModal = () => {
    const { unlinkOperationStatus } = this.state;

    return (
      <Dialog aria-describedby="dialog-content" open={true} onClose={this.closeUnlinkModal}>
        {unlinkOperationStatus === NetworkOperationState.SUCCESS ||
        unlinkOperationStatus === NetworkOperationState.FAILED ? (
          this.renderUnlinkOpCompleteComp()
        ) : (
          <UnlinkSlackConfirm onCancel={this.closeUnlinkModal} onConfirm={this.onUnLink} />
        )}
      </Dialog>
    );
  };

  render() {
    const { id, slackTeamDomainName } = this.props;
    const { email, status, linkOperationStatus, unlinkOperationStatus, showUnlinkModal } = this.state;

    const slackLink = slackTeamDomainName
      ? `https://${slackTeamDomainName}.slack.com`
      : 'https://slack.com/get-started';

    const inProgress =
      linkOperationStatus === NetworkOperationState.IN_PROGRESS ||
      unlinkOperationStatus === NetworkOperationState.IN_PROGRESS;
    const mode = status === SLACK_ACCOUNT_STATUS.UNLINKED ? VERIFY : LINKED;
    return (
      <div
        key={id}
        className="rc-SlackAccountComponent"
        style={inProgress ? { pointerEvents: 'none', cursor: 'progress' } : {}}
      >
        {showUnlinkModal && this.renderUnlinkModal()}

        {this.renderNotification()}

        <AccountLinkSlack
          mode={mode}
          email={email}
          errorState={linkOperationStatus === NetworkOperationState.FAILED}
          onLink={this.onLink}
          unlinkButton={mode === LINKED && <UnlinkSlackButton onClick={this.showUnlinkModal} />}
          slackLink={slackLink}
        />
      </div>
    );
  }
}

export default SlackAccountComponent;
